import {Injectable} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, NavigationStart} from '@angular/router';
import {Location} from '@angular/common';
import {loadModules} from 'esri-loader';

import {MapService} from '../map/map.service';
import {GoogleAnalyticsService} from '../googleAnalytics/google-analytics.service';
import {TableOfContentsService} from '../toc/table-of-contents.service';
import {ConfigService} from '../config/config.service';
import {BehaviorSubject, Subscription} from 'rxjs';

/**
 * Updates the URL with a hash link containing the current extent and the currently visible layers
 * Upon reloading the link, this service zooms to the extent and turns on the required layers
 */
@Injectable({
  providedIn: 'root'
})
export class BookmarkService {

  /**
   * Bookmark variable setup to keep track of stored info for the service
   *
   * @private
   */
  private bookmark = {
    xmin: 0,
    xmax: 0,
    ymin: 0,
    ymax: 0,
    layers: [],
    indexes: [],
    theme: ''
  };


  /**
   * Esri watch subscription to keep track of map extent changes
   *
   * @private
   */
  private mapViewObs;

  /**
   * Observable to watch when the map view reference is first created in order to zoom to extent in hash
   *
   * @private
   */
  private viewRefObs;

  /**
   * Observable to watch for when the toc layer list is created in order to open hash layers
   *
   * @private
   */
  private layerListObs;

  /**
   * Observable to watch for theme changes
   *
   * @private
   */
  private themeChangeObs;

  private urlUpdatedObs = new BehaviorSubject<boolean>(false);
  public getUrlUpdatedObs() { return this.urlUpdatedObs.asObservable(); }

  private firstRunFlag = false;
  private mapExt;
 
  /**
   * Constructor function to inject application variables into service
   *
   * @param map: Esri Map object
   * @param ga: Google Analytics
   * @param toc: Table of contents service to get info
   * @param route: Angular URL parsing service
   * @param location: Angular location service
   * @param router: Angular router service
   * @param config: Application configuration variables
   */
  constructor(private map: MapService, private ga: GoogleAnalyticsService, private toc: TableOfContentsService,
              private route: ActivatedRoute, private location: Location, private router: Router,
              private config: ConfigService) {}

 /**
   * Initializes the service after the page loads
   */
  public initService() {
    this.startupObservables();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (!this.firstRunFlag) {
          this.parseFragment();
        }
      }
    });
  }

  /**
   * Checks to see if there's a router fragment
   * If there is it loads the information to the right functions to update the map
   * Then it loads the map changing observables
   *
   * @private
   */
  private async parseFragment() {
    const fragmentSub = this.route.fragment.subscribe((fragment) => {
      let hashRoute;
      if (fragment) { hashRoute = fragment.split('?'); }
      if (!this.firstRunFlag) {
        if (hashRoute && hashRoute.length > 1 ) {
          this.viewRefObs = this.map.getViewRefObservable().subscribe( async (ref) =>  { 
            if (ref) {
              this.zoomToExtent(hashRoute);
              if (this.viewRefObs) {
                this.viewRefObs.unsubscribe();
              }              
            }
          }, (e) => {
            console.log(e);
          }, () => {
            this.viewRefObs.unsubscribe();
          });
        }
        
      } else {
        this.updateURL();
      }
      this.firstRunFlag = true;
    }, (e) => {
      console.log(e);
    }, () => {
      fragmentSub.unsubscribe();
    });
  }

  /**
   * Sets up the Esri watch utility to keep track of map movements
   */

  private startupObservables() {
    this.getTheme();
    if (this.map.getViewRef() && !this.mapViewObs) {
      this.mapViewObs = this.map.getViewRef().watch('stationary', () => {
        this.handleExtent();
      }
      );
    }
    
    if(!this.themeChangeObs) {
      this.themeChangeObs = this.toc.getThemeChangeObs().subscribe(() => {
        this.getTheme();
        this.parseFragment();
      });
    }
  }

  /**
   * Attempts a zoom to the extent specified in the URL
   *
   * @param hashRoute: Hash route from url
   * @private
   */
  private async zoomToExtent(hashRoute) {
    try {
      if (hashRoute.length > 1) {
        const coords = hashRoute[1].split(',');
        const [esriExtent] = await loadModules([
          'esri/geometry/Extent'
        ]);
        const extent = new esriExtent({
          xmin: coords[0],
          xmax: coords[1],
          ymin: coords[2],
          ymax: coords[3],
          spatialReference: 102100
        });
        this.bookmark.xmin = extent.xmin;
        this.bookmark.xmax = extent.xmax;
        this.bookmark.ymin = extent.ymin;
        this.bookmark.ymax = extent.ymax;
        this.map.getViewRef().goTo({
          target: extent
        });
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  /**
   * Grabs the current extent to the nearest whole number and adds it to the bookmark object
   * Then calls the update URL function
   */
  private handleExtent() {
    try {
      const extent = this.map.getMapExtent();
      if (extent) {
        this.bookmark.xmin = extent.xmin.toFixed(0);
        this.bookmark.xmax = extent.xmax.toFixed(0);
        this.bookmark.ymin = extent.ymin.toFixed(0);
        this.bookmark.ymax = extent.ymax.toFixed(0);
        this.updateURL();
      } else {
        setTimeout(() => {
          this.handleExtent();
        }, 250);
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }


   /**
   * Takes the bookmark object and creates the correct hash
   * 'current_location#xmin,xmax,ymin,ymax'
   */
  private updateURL() {
    try {
      if (this.router.url.search(/\b(\w*help\w*)\b/) === -1) {
        const url = this.router.createUrlTree([], {relativeTo: this.route}).toString();
        let urlString = url + '#' + this.bookmark.theme + '?'
          + this.bookmark.xmin + ',' + this.bookmark.xmax + ',' + this.bookmark.ymin + ',' + this.bookmark.ymax + '?';
        urlString = urlString.slice(0, -1);
        this.location.go(urlString);
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

 
  /**
   * Gets the current app theme
   */
  private getTheme() {
    const theme = this.toc.getTheme(this.toc.currentTheme);
    if (theme) { this.bookmark.theme = theme; }
  }
}
